import { useState, useEffect } from "react";
import { toast } from "sonner";
import { WhatsappLogo, ArrowsClockwise } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";

export const WhatsAppAdminCard = () => {
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState(null);
  const [qr, setQr] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState("");

  const loadStatus = () => api.get("/whatsapp/admin/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const w = r.data.whatsapp || {};
      setUrl(w.url || ""); setToken(w.token || "");
    }).catch(() => {});
    loadStatus();
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/settings", { section: "whatsapp", values: { url, token, session_id: "agentic-main" } });
      toast.success("WhatsApp settings saved"); loadStatus();
    } catch (e) { toast.error(errDetail(e)); }
    setBusy("");
  };
  const connect = async (restart = false) => {
    setBusy("connect");
    try {
      const { data } = await api.post("/whatsapp/admin/connect", { restart });
      setQr(data.qr_png_base64 || ""); toast.success(data.message); loadStatus();
    } catch (e) { toast.error(errDetail(e)); }
    setBusy("");
  };

  useEffect(() => {
    if (!qr) return;
    const iv = setInterval(async () => {
      try {
        const [{ data: q }, { data: s }] = await Promise.all([
          api.get("/whatsapp/admin/qr"), api.get("/whatsapp/admin/status")]);
        setStatus(s);
        if (["connected", "open", "online", "authenticated", "logged_in"].includes((s.state || "").toLowerCase())) {
          setQr(""); toast.success("WhatsApp connected!");
        } else if (q.qr_png_base64) setQr(q.qr_png_base64);
      } catch {}
    }, 12000);
    return () => clearInterval(iv);
  }, [qr]);
  const test = async () => {
    setBusy("test");
    try { await api.post("/whatsapp/admin/test", { phone: testPhone }); toast.success("Test message sent!"); }
    catch (e) { toast.error(errDetail(e)); }
    setBusy("");
  };

  const connected = ["connected", "open", "online", "authenticated", "logged_in"].includes((status?.state || "").toLowerCase());
  const stateLabel = !status ? "…" : !status.configured ? "Not configured"
    : status.state === "unreachable" ? "⚠ Waxum unreachable"
    : connected ? `Connected · +${status.phone}${status.push_name ? ` (${status.push_name})` : ""}`
    : `${status.state}${status.phone ? " · " + status.phone : ""}`;

  return (
    <div data-testid="wa-admin-card" className="bg-white border border-gray-200 rounded-md p-5 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <WhatsappLogo size={20} className="text-green-600" />
        <h3 className="font-heading font-bold text-gray-900">WhatsApp (Waxum)</h3>
        <span data-testid="wa-admin-status" className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${connected ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{stateLabel}</span>
        <button onClick={loadStatus} className="text-gray-400 hover:text-gray-700"><ArrowsClockwise size={13} /></button>
      </div>
      <p className="text-xs text-gray-500 mb-4">One company number for all users: summaries, urgent rule alerts, and chatting with the email agent (text + voice notes).</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <input data-testid="wa-admin-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://waxum.example.com" className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
        <input data-testid="wa-admin-token" value={token} onChange={(e) => setToken(e.target.value)}
          placeholder="Waxum superadmin token" className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="wa-admin-save" onClick={save} disabled={busy === "save"}
          className="text-sm font-semibold bg-gray-900 text-white rounded-md px-3 py-2 disabled:opacity-50">Save</button>
        <button data-testid="wa-admin-connect" onClick={() => connect(false)} disabled={busy === "connect" || !url}
          className="text-sm font-semibold bg-green-600 text-white rounded-md px-3 py-2 disabled:opacity-50">Connect number (QR)</button>
        <button data-testid="wa-admin-restart" onClick={() => connect(true)} disabled={busy === "connect" || !url}
          className="text-sm font-semibold border border-amber-300 text-amber-700 rounded-md px-3 py-2 disabled:opacity-50">Restart pairing</button>
        <input data-testid="wa-admin-test-phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
          placeholder="Test phone +31…" className="border border-gray-300 rounded-md px-3 py-2 text-sm w-40" />
        <button data-testid="wa-admin-test-btn" onClick={test} disabled={busy === "test" || !testPhone.trim()}
          className="text-sm font-semibold border border-gray-300 rounded-md px-3 py-2 disabled:opacity-50">Send test message</button>
      </div>
      {qr && (
        <div data-testid="wa-admin-qr" className="mt-4">
          <p className="text-xs text-gray-500 mb-2">Scan with WhatsApp → Linked devices — <b>this QR auto-refreshes every 12s</b>, scan the one on screen right away:</p>
          <img src={`data:image/png;base64,${qr}`} alt="WhatsApp QR" className="w-48 h-48 border border-gray-200 rounded" />
          <p className="text-[11px] text-green-700 mt-1">🔄 Live — always showing the newest code</p>
        </div>
      )}
    </div>
  );
};
